/** The Receipts tab: everything captured so far, newest first, and the way to add another. */

import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Notice } from "@/components/ui/Field";
import { Amount, PurchaseDate, StatusBadge, StoreName } from "./ReceiptBits";
import { receiptsQuery } from "./queries";

export function ReceiptsPage() {
  const { t } = useTranslation();
  const receipts = useInfiniteQuery(receiptsQuery);
  const items = receipts.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-stone-900">{t("receipts.title")}</h1>
        <Link
          to="/receipts/new"
          className="bg-brand-700 hover:bg-brand-800 inline-flex min-h-11 items-center gap-1.5 rounded-lg px-4 text-base font-medium text-white"
        >
          <Plus className="h-5 w-5" aria-hidden />
          {t("receipts.add")}
        </Link>
      </header>

      {receipts.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {receipts.isError && <Notice tone="error">{t("receipts.errors.loadFailed")}</Notice>}

      {receipts.isSuccess && items.length === 0 && (
        <Notice tone="info">{t("receipts.empty")}</Notice>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {items.map((receipt) => (
            <li key={receipt.id}>
              <Link
                to="/receipts/$receiptId"
                params={{ receiptId: receipt.id }}
                className="flex min-h-16 items-center gap-3 px-4 py-3 hover:bg-stone-50"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="truncate font-medium text-stone-800">
                    <StoreName name={receipt.store_name} />
                  </p>
                  <p className="flex items-center gap-2 text-sm text-stone-500">
                    <PurchaseDate date={receipt.purchase_date} />
                    <StatusBadge status={receipt.status} />
                  </p>
                </div>
                <Amount money={receipt.total} className="font-medium text-stone-800" />
                <ChevronRight className="h-5 w-5 shrink-0 text-stone-300" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {receipts.hasNextPage && (
        <Button
          variant="secondary"
          disabled={receipts.isFetchingNextPage}
          onClick={() => void receipts.fetchNextPage()}
        >
          {receipts.isFetchingNextPage ? t("common.loading") : t("receipts.loadMore")}
        </Button>
      )}
    </section>
  );
}
